import { freeShippingThreshold } from "@/config";
import type { OptionsDataDeliveryDates } from "@/pages/UserStore/Store/services"; // Assuming path from useCart.ts
import type { CartProduct, ShippingType } from "@/services/types"; // Assuming path from useCart.ts
import type { Product } from "@/services/types"; // Also need Product type for ADD_ITEM
import type React from "react";
import {
    type Dispatch,
    type ReactNode,
    createContext,
    useContext,
    useReducer,
} from "react";

// --- State Structure ---

/**
 * Represents the UI state related to the cart and checkout process.
 */
export interface CartUIState {
    cartViewIsOpen: boolean; // Controls visibility of the cart view
    showConfirmation: boolean; // Controls visibility of the purchase confirmation step/modal
    showCartDetails: boolean; // Controls visibility of the detailed cart view/sidebar
    cartProductDetail?: CartProduct; // Stores the product currently being viewed/edited in detail
    confirmationOpen: boolean; // Controls a specific confirmation dialog (e.g., before placing order)
}

/**
 * Represents the overall state managed by the CartStateContext.
 * Includes cart items, order summary calculations, delivery info, UI flags, and errors.
 */
export interface CartState {
    shippingType: ShippingType;
    items: CartProduct[]; // List of products currently in the cart
    error: string | null; // Stores any error message related to cart operations
    deliveryDate?: OptionsDataDeliveryDates; // Selected delivery date and associated cost
    deliveryDateUpsell?: OptionsDataDeliveryDates; // Selected delivery date for upsell and associated cost
    subscriptionDate?: string; // Selected subscription date and associated cost
    isUpsell: boolean; // Flag indicating if the current context is an upsell purchase
    ui: CartUIState; // Nested object for UI-related state
    // Order Summary - calculated values stored in state
    subtotal: number; // Total cost of items before shipping and discounts
    shippingCost: number; // Cost of shipping, potentially zero for upsells or based on deliveryDate
    discountAmount: {
        flat: number; // Flat amount deducted from the subtotal (e.g., from promotions or upsells)
        percentage: number; // Percentage deducted from the subtotal (e.g., from promotions or upsells)
        amount?: number; // Total amount deducted from the subtotal (flat + percentage)
    }; // Amount deducted from the subtotal (e.g., from promotions or upsells)
    total: number; // Final calculated total (subtotal + shippingCost - discountAmount)
    // NEW FIELDS for delivery management
    dataDeliveryDates: OptionsDataDeliveryDates[] | null; // Available delivery dates/options
    subscriptionName: string | null; // Name of the subscription (if applicable)
}

// --- Helper Functions ---

/**
 * Calculates subtotal, shipping cost, and total based on cart items,
 * delivery date, upsell status, and discount.
 */
const calculateTotals = (
    items: CartProduct[],
    deliveryDate?: OptionsDataDeliveryDates,
    isUpsell = false,
    discountAmount: { flat: number; percentage: number } = {
        flat: 0,
        percentage: 0,
    },
): { subtotal: number; shippingCost: number; total: number } => {
    const subtotal = items.reduce(
        (sum, item) => sum + item.price * item.quantity,
        0,
    );
    const hasFreeShipping = subtotal >= freeShippingThreshold;
    const shippingCost =
        isUpsell || hasFreeShipping ? 0 : (deliveryDate?.shippingCost ?? 0);
    const total =
        subtotal +
        shippingCost -
        discountAmount.flat -
        Math.trunc((subtotal * discountAmount.percentage) / 100);
    return { subtotal, shippingCost, total };
};

// --- Action Types ---

export enum ActionType {
    ADD_ITEM = "ADD_ITEM",
    REMOVE_ITEM = "REMOVE_ITEM",
    UPDATE_QUANTITY = "UPDATE_QUANTITY",
    CLEAR_CART = "CLEAR_CART",
    SET_DELIVERY_DATE = "SET_DELIVERY_DATE",
    SET_UPSELL_STATUS = "SET_UPSELL_STATUS",
    APPLY_FLAT_DISCOUNT = "APPLY_FLAT_DISCOUNT",
    APPLY_PERCENTAGE_DISCOUNT = "APPLY_PERCENTAGE_DISCOUNT",
    SET_ERROR = "SET_ERROR",
    CLEAR_ERROR = "CLEAR_ERROR",
    // UI Actions
    SET_CART_VIEW_IS_OPEN = "SET_CART_VIEW_IS_OPEN",
    SET_CART_VIEW_IS_CLOSED = "SET_CART_VIEW_IS_CLOSED",
    SET_SHOW_CONFIRMATION = "SET_SHOW_CONFIRMATION",
    SET_SHOW_CART_DETAILS = "SET_SHOW_CART_DETAILS",
    SET_CART_PRODUCT_DETAIL = "SET_CART_PRODUCT_DETAIL",
    SET_CONFIRMATION_OPEN = "SET_CONFIRMATION_OPEN",
    // NEW ACTIONS for delivery management
    SET_SHIPPING_COST = "SET_SHIPPING_COST", // Note: Allows manual override of calculated cost
    SET_DATA_DELIVERY_DATES = "SET_DATA_DELIVERY_DATES",
    SET_SUBSCRIPTION_NAME = "SET_SUBSCRIPTION_NAME",
    SET_SUBSCRIPTION_DATE = "SET_SUBSCRIPTION_DATE",
    SET_SHIPPING_TYPE = "SET_SHIPPING_TYPE",
}

// --- Action Payloads & Discriminated Union ---

type ActionMap = {
    [ActionType.ADD_ITEM]: { product: Product; quantity: number };
    [ActionType.REMOVE_ITEM]: { productId: string | number };
    [ActionType.UPDATE_QUANTITY]: {
        productId: string | number;
        quantity: number;
    };
    [ActionType.CLEAR_CART]: undefined;
    [ActionType.SET_DELIVERY_DATE]: { date?: OptionsDataDeliveryDates };
    [ActionType.SET_SUBSCRIPTION_DATE]: { date?: string };
    [ActionType.SET_UPSELL_STATUS]: { isUpsell: boolean };
    [ActionType.APPLY_FLAT_DISCOUNT]: { amount: number };
    [ActionType.APPLY_PERCENTAGE_DISCOUNT]: { amount: number };
    [ActionType.SET_ERROR]: { error: string | null };
    [ActionType.CLEAR_ERROR]: undefined;
    // UI Actions
    [ActionType.SET_CART_VIEW_IS_OPEN]: undefined;
    [ActionType.SET_CART_VIEW_IS_CLOSED]: undefined;
    [ActionType.SET_SHOW_CONFIRMATION]: { show: boolean };
    [ActionType.SET_SHOW_CART_DETAILS]: { show: boolean };
    [ActionType.SET_CART_PRODUCT_DETAIL]: { product?: CartProduct };
    [ActionType.SET_CONFIRMATION_OPEN]: { open: boolean };
    // NEW ACTION PAYLOADS
    [ActionType.SET_SHIPPING_COST]: { cost: number };
    [ActionType.SET_DATA_DELIVERY_DATES]: {
        dates: OptionsDataDeliveryDates[] | null;
    };
    [ActionType.SET_SUBSCRIPTION_NAME]: { name: string | null };
    [ActionType.SET_SHIPPING_TYPE]: { shippingType: ShippingType };
};

export type CartAction = {
    [Key in keyof ActionMap]: {
        type: Key;
        payload: ActionMap[Key];
    };
}[keyof ActionMap];

// --- Reducer ---

const ensureStringId = (id: string | number): string => String(id);

/**
 * Reducer function to manage CartState based on dispatched actions.
 */
export const cartReducer = (
    state: CartState,
    action: CartAction,
): CartState => {
    // Keep track if totals need recalculation
    let needsRecalculation = false;
    // Start with a shallow copy for immutable updates
    const updatedState = { ...state };

    switch (action.type) {
        case ActionType.ADD_ITEM: {
            const { product, quantity } = action.payload;
            const productIdStr = ensureStringId(product.id);
            const existingItemIndex = state.items.findIndex(
                (item) => ensureStringId(item.id) === productIdStr,
            );
            let newItems: CartProduct[];
            if (existingItemIndex > -1) {
                // Update quantity if item exists
                newItems = state.items.map((item, index) =>
                    index === existingItemIndex
                        ? { ...item, quantity: item.quantity + quantity }
                        : item,
                );
            } else {
                // Add new item
                const newCartItem: CartProduct = {
                    ...product,
                    quantity,
                };
                newItems = [...state.items, newCartItem];
            }
            updatedState.ui.cartViewIsOpen = true; // Open cart view when adding an item
            updatedState.items = newItems;
            needsRecalculation = true;
            break;
        }

        case ActionType.REMOVE_ITEM: {
            const productIdStr = ensureStringId(action.payload.productId);
            updatedState.items = state.items.filter(
                (item) => ensureStringId(item.id) !== productIdStr,
            );
            needsRecalculation = true;
            break;
        }

        case ActionType.UPDATE_QUANTITY: {
            const { productId, quantity } = action.payload;
            const productIdStr = ensureStringId(productId);
            let newItems = state.items.map((item) =>
                ensureStringId(item.id) === productIdStr
                    ? { ...item, quantity }
                    : item,
            );
            // Remove item if quantity is zero or less
            newItems = newItems.filter((item) => item.quantity > 0);
            updatedState.items = newItems;
            needsRecalculation = true;
            break;
        }

        case ActionType.CLEAR_CART: {
            updatedState.items = [];
            // Reset other relevant states
            updatedState.discountAmount.flat = 0;
            updatedState.discountAmount.percentage = 0;
            updatedState.deliveryDate = undefined;
            updatedState.shippingCost = 0; // Reset shipping cost
            updatedState.dataDeliveryDates = null;
            updatedState.subscriptionName = null;
            updatedState.isUpsell = false;
            needsRecalculation = true; // Recalculate totals (subtotal 0, shipping 0, total 0)
            break;
        }

        case ActionType.SET_DELIVERY_DATE: {
            updatedState.deliveryDate = action.payload.date;
            needsRecalculation = true;
            break;
        }

        case ActionType.SET_SUBSCRIPTION_DATE: {
            updatedState.subscriptionDate = action.payload.date;
            needsRecalculation = true;
            break;
        }

        case ActionType.SET_UPSELL_STATUS: {
            const newIsUpsell = action.payload.isUpsell;
            updatedState.isUpsell = newIsUpsell;
            // Reset delivery date and discount when entering upsell mode
            if (newIsUpsell) {
                updatedState.deliveryDate = undefined;
                updatedState.discountAmount.flat = 0;
                updatedState.discountAmount.percentage = 0;
            }
            needsRecalculation = true;
            break;
        }

        case ActionType.APPLY_FLAT_DISCOUNT: {
            updatedState.discountAmount.flat = action.payload.amount;
            needsRecalculation = true;
            break;
        }

        case ActionType.APPLY_PERCENTAGE_DISCOUNT: {
            updatedState.discountAmount.percentage = action.payload.amount;
            needsRecalculation = true;
            break;
        }

        // --- NEW Reducers ---
        case ActionType.SET_SHIPPING_COST: {
            // Directly set shipping cost and recalculate total based on this manual cost.
            // Return early to prevent override by the final recalculation logic below.
            const manualShippingCost = action.payload.cost;
            const total = updatedState.subtotal + manualShippingCost;
            // Return the state with manually updated shippingCost and total
            return { ...updatedState, shippingCost: manualShippingCost, total };
        }

        case ActionType.SET_DATA_DELIVERY_DATES:
            // Only update the data, doesn't affect totals directly. Return early.
            return { ...updatedState, dataDeliveryDates: action.payload.dates };

        case ActionType.SET_SUBSCRIPTION_NAME:
            // Only update the name, doesn't affect totals directly. Return early.
            return { ...updatedState, subscriptionName: action.payload.name };

        // --- Error & UI Reducers (Return early, no recalculation needed) ---
        case ActionType.SET_ERROR:
            return { ...updatedState, error: action.payload.error }; // Update error and return early

        case ActionType.CLEAR_ERROR:
            return { ...updatedState, error: null }; // Update error and return early

        // --- UI Reducers ---

        case ActionType.SET_CART_VIEW_IS_OPEN:
            return {
                ...updatedState,
                ui: { ...updatedState.ui, cartViewIsOpen: true },
            };
        case ActionType.SET_CART_VIEW_IS_CLOSED:
            return {
                ...updatedState,
                ui: { ...updatedState.ui, cartViewIsOpen: false },
            };
        // Show confirmation modal
        case ActionType.SET_SHOW_CONFIRMATION:
            return {
                ...updatedState,
                ui: {
                    ...updatedState.ui,
                    showConfirmation: action.payload.show,
                },
            };

        case ActionType.SET_SHOW_CART_DETAILS:
            return {
                ...updatedState,
                ui: {
                    ...updatedState.ui,
                    showCartDetails: action.payload.show,
                },
            };

        case ActionType.SET_CART_PRODUCT_DETAIL:
            return {
                ...updatedState,
                ui: {
                    ...updatedState.ui,
                    cartProductDetail: action.payload.product,
                },
            };

        case ActionType.SET_CONFIRMATION_OPEN:
            return {
                ...updatedState,
                ui: {
                    ...updatedState.ui,
                    confirmationOpen: action.payload.open,
                },
            };

        case ActionType.SET_SHIPPING_TYPE:
            return {
                ...updatedState,
                shippingType: action.payload.shippingType,
            };

        default:
            // Ensures exhaustiveness check for action types
            // const _: never = action;
            // console.warn(`Unhandled action type: ${action.type}`);
            return updatedState; // Return current state if action is not recognized
    }

    // Recalculate totals if needed for actions that affect items, delivery, upsell, or discount
    if (needsRecalculation) {
        const { subtotal, shippingCost, total } = calculateTotals(
            updatedState.items,
            updatedState.deliveryDate,
            updatedState.isUpsell,
            updatedState.discountAmount,
        );
        // Assign calculated values. This recalculates shippingCost based on standard logic.
        updatedState.subtotal = subtotal;
        updatedState.shippingCost = shippingCost;
        updatedState.total = total;
    }

    // Return the updated state
    return updatedState;
};

// --- Initial State ---

/**
 * The initial state for the cart context when the application loads or context is reset.
 */
export const initialState: CartState = {
    items: [],
    error: null,
    deliveryDate: undefined,
    isUpsell: false,
    ui: {
        cartViewIsOpen: false,
        showConfirmation: false,
        showCartDetails: false,
        cartProductDetail: undefined,
        confirmationOpen: false,
    },
    // Initial calculation based on defaults
    subtotal: 0,
    shippingCost: 0, // Initialize shippingCost to 0, rely on calculation or SET_DELIVERY_DATE
    discountAmount: {
        flat: 0, // Initial flat discount is 0
        percentage: 0, // Initial percentage discount is 0
    },
    total: 0, // Initial total is 0
    // NEW Initial Values
    dataDeliveryDates: null,
    subscriptionName: null,
    shippingType: "PICKUP",
};

// --- Context Objects ---

/**
 * Context to provide the cart state.
 */
const CartStateContext = createContext<CartState | undefined>(undefined);

/**
 * Context to provide the dispatch function for cart actions.
 */
const CartDispatchContext = createContext<Dispatch<CartAction> | undefined>(
    undefined,
);

// --- Context Provider Component ---

interface CartProviderProps {
    children: ReactNode;
}

/**
 * Provider component that wraps parts of the application needing access to the cart state and actions.
 * It initializes the state using useReducer and provides state and dispatch via context.
 */
export const CartProvider: React.FC<CartProviderProps> = ({ children }) => {
    const [state, dispatch] = useReducer(cartReducer, initialState);

    return (
        <CartStateContext.Provider value={state}>
            <CartDispatchContext.Provider value={dispatch}>
                {children}
            </CartDispatchContext.Provider>
        </CartStateContext.Provider>
    );
};

// --- Custom Hooks for Access ---

/**
 * Custom hook to access the cart state.
 * Throws an error if used outside of a CartProvider.
 */
export const useCartState = (): CartState => {
    const context = useContext(CartStateContext);
    if (context === undefined) {
        throw new Error("useCartState must be used within a CartProvider");
    }
    return context;
};

/**
 * Custom hook to access the cart dispatch function.
 * Throws an error if used outside of a CartProvider.
 */
export const useCartDispatch = (): Dispatch<CartAction> => {
    const context = useContext(CartDispatchContext);
    if (context === undefined) {
        throw new Error("useCartDispatch must be used within a CartProvider");
    }
    return context;
};
